import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { Reveal, Eyebrow, SectionTitle } from "./Reveal";
import { GALLERY_CATEGORIES, galleryCategoryLabel, imgSrc } from "../../lib/api";

export function GallerySection({ images }) {
  const sorted = useMemo(() => [...images].sort((a, b) => (a.placeholder ? 1 : 0) - (b.placeholder ? 1 : 0)), [images]);
  const cats = useMemo(() => {
    const present = new Set(sorted.map((i) => i.category));
    return GALLERY_CATEGORIES.filter((c) => present.has(c.id));
  }, [sorted]);
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(null);
  const filtered = filter === "all" ? sorted : sorted.filter((i) => i.category === filter);

  const step = (dir) => {
    if (open === null) return;
    setOpen((open + dir + filtered.length) % filtered.length);
  };

  if (!images.length) return null;

  return (
    <section id="galleria" className="py-24 sm:py-32 bg-slate2 border-y border-white/5" data-testid="gallery-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <Reveal>
          <Eyebrow>Galleria</Eyebrow>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="mt-4 flex flex-wrap items-end justify-between gap-6">
            <SectionTitle>
              Vivi l'atmosfera <em className="italic text-gold-glow">Red Stone</em>
            </SectionTitle>
            <p className="text-xs text-cream-subtle max-w-xs leading-relaxed">
              Gli scatti del locale, dei piatti e delle serate al Red Stone.
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="mt-9 flex gap-2 overflow-x-auto no-scrollbar pb-1 -mx-5 px-5 sm:mx-0 sm:px-0">
            {[{ id: "all", label: "Tutte" }, ...cats].map((c) => (
              <button
                key={c.id}
                data-testid={`gallery-filter-${c.id}`}
                onClick={() => setFilter(c.id)}
                className={`shrink-0 text-[11px] font-bold uppercase tracking-[0.2em] px-5 py-2.5 rounded-full border transition-all duration-300 ${
                  filter === c.id
                    ? "bg-burgundy-brand border-gold/40 text-cream"
                    : "border-white/10 text-cream-subtle hover:border-gold/40 hover:text-cream"
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
        </Reveal>

        <div className="mt-8 columns-2 md:columns-3 gap-3 sm:gap-4">
          {filtered.map((img, idx) => (
            <motion.button
              key={img.id}
              data-testid={`gallery-image-${img.id}`}
              onClick={() => setOpen(idx)}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.6, delay: (idx % 3) * 0.08 }}
              className="relative block w-full mb-3 sm:mb-4 break-inside-avoid overflow-hidden rounded-xl border border-white/10 group"
            >
              <img
                src={imgSrc(img.url)}
                alt={img.caption || `Galleria Red Stone — ${galleryCategoryLabel(img.category)}`}
                loading="lazy"
                className="w-full object-cover transition-transform duration-700 group-hover:scale-108"
              />
              <span className="absolute inset-0 bg-gradient-to-t from-obsidian/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <span className="absolute bottom-3 left-3 right-3 flex items-center justify-between opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                <span className="text-[10px] uppercase tracking-[0.2em] text-cream">{img.caption || galleryCategoryLabel(img.category)}</span>
                {img.placeholder && <span className="text-[9px] uppercase tracking-[0.15em] text-cream-subtle">archivio</span>}
              </span>
            </motion.button>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {open !== null && filtered[open] && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[80] bg-obsidian/95 backdrop-blur-xl flex items-center justify-center p-4"
            data-testid="gallery-lightbox-modal"
            onClick={() => setOpen(null)}
          >
            <button data-testid="lightbox-close" aria-label="Chiudi" onClick={() => setOpen(null)} className="absolute top-5 right-5 text-cream hover:text-gold-glow p-2 transition-colors z-10">
              <X className="w-7 h-7" />
            </button>
            <button data-testid="lightbox-prev" aria-label="Precedente" onClick={(e) => { e.stopPropagation(); step(-1); }} className="absolute left-3 sm:left-6 text-cream hover:text-gold-glow p-2 transition-colors z-10">
              <ChevronLeft className="w-9 h-9" />
            </button>
            <button data-testid="lightbox-next" aria-label="Successiva" onClick={(e) => { e.stopPropagation(); step(1); }} className="absolute right-3 sm:right-6 text-cream hover:text-gold-glow p-2 transition-colors z-10">
              <ChevronRight className="w-9 h-9" />
            </button>
            <motion.figure
              key={filtered[open].id}
              initial={{ opacity: 0, scale: 0.94 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="max-w-4xl w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <img src={imgSrc(filtered[open].url)} alt={filtered[open].caption || "Foto Red Stone"} className="w-full max-h-[78vh] object-contain rounded-lg" />
              <figcaption className="mt-4 text-center text-xs uppercase tracking-[0.25em] text-cream-muted">
                {filtered[open].caption || galleryCategoryLabel(filtered[open].category)}
                {filtered[open].placeholder && <span className="text-cream-subtle"> — foto d'archivio</span>}
              </figcaption>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
